/* eslint-disable complexity */
import { FormInstance } from 'antd';
import Image from 'next/image';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AiOutlineCalendar,
  AiOutlineCheck,
  AiOutlineCheckCircle,
  AiOutlineCreditCard,
  AiOutlineInfoCircle,
} from 'react-icons/ai';

import cloudinaryUrl from '@/utils/cloudinary';
import { getCurrencySymbol } from '@/utils/currency';
import { BookingCost, DaypassOffices, Location } from '@core/booking/booking.types';

import BookingCard, { DayPassInfoState } from './BookingCard';

export interface DayPassSettings {
  title?: string | null;
  description?: string | null;
  value?: string | null;
  hint?: string | null;
}

interface ConfigureStepProps {
  form: FormInstance;
  dayPassSettings: DayPassSettings | null;
  locations: Location[];
  selectedLocation: Location | null;
  selectedLocationId: string | null;
  setSelectedLocationId: (id: string) => void;
  isReserved: boolean;
  dayPassInfo: DayPassInfoState;
  onToggleDate: (date: string) => void;
  onRemoveDate: (date: string) => void;
  onRangeChange: (startDate: string | null, endDate: string | null) => void;
  allowedOffices: DaypassOffices[];
  selectedUnitId: string | null;
  setSelectedUnitId: (id: string) => void;
  bookingCost: BookingCost | null;
  dayCount: number;
  perDayPrice: string;
  onContinue: () => void;
  canContinue: boolean;
}

const HOW_IT_WORKS_STEPS = [
  { Icon: AiOutlineCalendar, titleKey: 'dayPass.howItWorks.step1', captionKey: 'dayPass.howItWorks.step1Caption' },
  { Icon: AiOutlineCreditCard, titleKey: 'dayPass.howItWorks.step2', captionKey: 'dayPass.howItWorks.step2Caption' },
  { Icon: AiOutlineCheckCircle, titleKey: 'dayPass.howItWorks.step3', captionKey: 'dayPass.howItWorks.step3Caption' },
];

/**
 * Screen 0 of the day-pass journey: product-style page (hero, description,
 * how-it-works, good-to-know) with the booking widget alongside — desktop
 * gets a sticky card, mobile gets the card inline plus a sticky bottom bar.
 *
 * The BookingCard renders exactly once (it registers antd Form fields, so
 * duplicating it would double-register names); mobile/desktop placement is
 * done with grid column/row assignment instead.
 */
const ConfigureStep: FC<ConfigureStepProps> = ({
  form,
  dayPassSettings,
  locations,
  selectedLocation,
  selectedLocationId,
  setSelectedLocationId,
  isReserved,
  dayPassInfo,
  onToggleDate,
  onRemoveDate,
  onRangeChange,
  allowedOffices,
  selectedUnitId,
  setSelectedUnitId,
  bookingCost,
  dayCount,
  perDayPrice,
  onContinue,
  canContinue,
}) => {
  const { t } = useTranslation();
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [descriptionOverflows, setDescriptionOverflows] = useState(false);
  const descriptionRef = useRef<HTMLParagraphElement>(null);

  const heroImage = dayPassSettings?.value;
  const title = dayPassSettings?.title || t('bookingSteps.bookDaypass');
  const description = dayPassSettings?.description || t('dayPass.defaultDescription');
  const currencySymbol = getCurrencySymbol(undefined, selectedLocation);
  const hasCost = Number(bookingCost?.total) > 0;
  // Sticky-bar amount: the VAT-inclusive total once fetched, the per-day
  // price before that, nothing when the tenant exposes neither.
  let barAmount = '';
  if (hasCost) {
    barAmount = `${currencySymbol}${(bookingCost as BookingCost).total.toFixed(2)}`;
  } else if (perDayPrice) {
    barAmount = `${currencySymbol}${perDayPrice}`;
  }

  useEffect(() => {
    const el = descriptionRef.current;
    if (el) setDescriptionOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [description]);

  return (
    <div className="pb-28 md:pb-0">
      <div className="max-w-6xl mx-auto flex flex-col md:grid md:grid-cols-[minmax(0,1fr)_380px] md:items-start gap-6">
        {/* Intro — hero, title, description, hint */}
        <div className="min-w-0 md:col-start-1 md:row-start-1">
          {heroImage && (
            <div className="relative w-full overflow-hidden rounded-2xl h-48 md:h-80">
              <Image src={cloudinaryUrl(heroImage, 1200)} alt={title} fill style={{ objectFit: 'cover' }} unoptimized />
            </div>
          )}

          <h1 className="text-2xl md:text-3xl font-bold m-0 mt-5" style={{ color: '#404D61' }}>
            {title}
          </h1>

          <p
            ref={descriptionRef}
            className={`text-base leading-relaxed m-0 mt-2 max-w-[65ch] ${
              descriptionExpanded ? '' : 'line-clamp-3 md:line-clamp-none'
            }`}
            style={{ color: '#707070' }}
          >
            {description}
          </p>
          {descriptionOverflows && !descriptionExpanded && (
            <button
              type="button"
              className="md:hidden text-sm font-medium bg-transparent border-none p-0 mt-1 cursor-pointer"
              style={{ color: 'var(--main)' }}
              onClick={() => setDescriptionExpanded(true)}
            >
              {t('dayPass.readMore')}
            </button>
          )}

          {dayPassSettings?.hint && (
            <div
              className="flex gap-3 rounded-xl p-4 mt-4"
              style={{ backgroundColor: '#F8F9FA', borderInlineStart: '3px solid var(--main)' }}
            >
              <AiOutlineInfoCircle size={18} style={{ color: 'var(--main)', flexShrink: 0, marginTop: 2 }} />
              <p className="text-sm leading-relaxed m-0" style={{ color: '#404D61' }}>
                {dayPassSettings.hint}
              </p>
            </div>
          )}
        </div>

        {/* Booking card — mobile: inline after the intro; desktop: sticky right column */}
        <div className="md:col-start-2 md:row-start-1 md:row-span-2 md:sticky md:top-6 w-full md:w-[380px]">
          <BookingCard
            form={form}
            locations={locations}
            selectedLocation={selectedLocation}
            selectedLocationId={selectedLocationId}
            setSelectedLocationId={setSelectedLocationId}
            isReserved={isReserved}
            dayPassInfo={dayPassInfo}
            onToggleDate={onToggleDate}
            onRemoveDate={onRemoveDate}
            onRangeChange={onRangeChange}
            allowedOffices={allowedOffices}
            selectedUnitId={selectedUnitId}
            setSelectedUnitId={setSelectedUnitId}
            bookingCost={bookingCost}
            dayCount={dayCount}
            perDayPrice={perDayPrice}
            onContinue={onContinue}
            canContinue={canContinue}
          />
        </div>

        {/* Extras — how it works + good to know (always rendered so the page is never bare) */}
        <div className="min-w-0 md:col-start-1 md:row-start-2">
          <div className="pt-2 md:pt-0">
            <div className="pt-6 md:pt-8" style={{ borderTop: '1px solid #E5E7EB' }}>
              <h2 className="text-lg font-bold m-0 mb-4" style={{ color: '#404D61' }}>
                {t('dayPass.howItWorks.title')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {HOW_IT_WORKS_STEPS.map(({ Icon, titleKey, captionKey }) => (
                  <div key={titleKey} className="rounded-xl p-4" style={{ border: '1px solid #E5E7EB' }}>
                    <div
                      className="flex items-center justify-center w-10 h-10 rounded-full"
                      style={{ backgroundColor: '#F8F9FA' }}
                    >
                      <Icon size={20} style={{ color: 'var(--main)' }} />
                    </div>
                    <h3 className="text-sm font-semibold m-0 mt-3" style={{ color: '#404D61' }}>
                      {t(titleKey)}
                    </h3>
                    <p className="text-xs m-0 mt-1 leading-relaxed" style={{ color: '#707070' }}>
                      {t(captionKey)}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8">
              <h2 className="text-lg font-bold m-0 mb-2" style={{ color: '#404D61' }}>
                {t('dayPass.goodToKnow.title')}
              </h2>
              {selectedLocation && (
                <div className="flex gap-4 py-3" style={{ borderBottom: '1px solid #E5E7EB' }}>
                  <AiOutlineCheck size={16} style={{ color: 'var(--main)', marginTop: 2, flexShrink: 0 }} />
                  <span className="w-36 shrink-0 text-sm font-semibold" style={{ color: '#404D61' }}>
                    {t('dayPass.goodToKnow.location')}
                  </span>
                  <span className="text-sm" style={{ color: '#707070' }}>
                    {selectedLocation.name}
                    {selectedLocation.address ? ` · ${selectedLocation.address}` : ''}
                  </span>
                </div>
              )}
              {perDayPrice && (
                <div className="flex gap-4 py-3" style={{ borderBottom: '1px solid #E5E7EB' }}>
                  <AiOutlineCheck size={16} style={{ color: 'var(--main)', marginTop: 2, flexShrink: 0 }} />
                  <span className="w-36 shrink-0 text-sm font-semibold" style={{ color: '#404D61' }}>
                    {t('dayPass.goodToKnow.price')}
                  </span>
                  <span className="text-sm" style={{ color: '#707070' }}>
                    <span dir="ltr">
                      {currencySymbol}
                      {perDayPrice}
                    </span>
                    {` / ${t('dayPass.perDay')}`}
                  </span>
                </div>
              )}
              <div className="flex gap-4 py-3" style={{ borderBottom: '1px solid #E5E7EB' }}>
                <AiOutlineCheck size={16} style={{ color: 'var(--main)', marginTop: 2, flexShrink: 0 }} />
                <span className="w-36 shrink-0 text-sm font-semibold" style={{ color: '#404D61' }}>
                  {t('dayPass.goodToKnow.bookingType')}
                </span>
                <span className="text-sm" style={{ color: '#707070' }}>
                  {isReserved ? t('dayPass.goodToKnow.reservedNote') : t('dayPass.goodToKnow.flexNote')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sticky bottom bar — the single CTA on small screens */}
      <div
        className="md:hidden fixed bottom-0 inset-x-0 z-20 bg-white px-4 pt-3 flex items-center justify-between gap-3"
        style={{
          borderTop: '1px solid #E5E7EB',
          boxShadow: '0 -2px 8px rgba(16,24,40,0.06)',
          paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))',
        }}
      >
        <div className="flex flex-col min-w-0">
          <span className="text-xs" style={{ color: '#707070' }}>
            {t('dayPass.daysCount', { count: dayCount })}
          </span>
          {barAmount && (
            <span className="text-lg font-bold" style={{ color: 'var(--main)' }} dir="ltr">
              {barAmount}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onContinue}
          disabled={!canContinue}
          className="px-8 py-3.5 rounded-xl text-base font-semibold border-none cursor-pointer"
          style={{
            backgroundColor: canContinue ? 'var(--main)' : '#E5E7EB',
            color: canContinue ? '#FFFFFF' : '#9CA3AF',
          }}
        >
          {t('bookingSteps.continue')}
        </button>
      </div>
    </div>
  );
};

export default ConfigureStep;
